/** Сравнение выбранных результатов: выбор галочками, адрес, «паспорт» параметров запуска, центр лупы. Чистый TS, без React: гоняется node:test. */

export const COMPARE_MAX = 4;
export const COMPARE_MIN = 2;
export const LOUPE_SCALE = 2.5;

/** Есть — убрать; нет и есть место — дописать в конец; иначе без изменений. */
export function toggleSelect(ids: number[], id: number, max = COMPARE_MAX): number[] {
  if (ids.includes(id)) return ids.filter((x) => x !== id);
  return ids.length < max ? [...ids, id] : ids;
}

/** `ids` из адреса: положительные целые, без повторов, не больше COMPARE_MAX. */
export function parseCompareIds(raw: string | null): number[] {
  if (!raw) return [];
  const out: number[] = [];
  for (const part of raw.split(",")) {
    if (!/^\d+$/.test(part.trim())) continue;
    const n = Number(part);
    if (n > 0 && Number.isSafeInteger(n) && !out.includes(n)) out.push(n);
    if (out.length >= COMPARE_MAX) break;
  }
  return out;
}

export const compareHref = (ids: number[]): string => "/results/compare?ids=" + ids.join(",");

/** Минимальная форма `ResultCard`, нужная паспорту. */
export type PassportCard = {
  variant: { id: number };
  generation: {
    kind?: string;
    model: string;
    quality: string | null;
    size: string | null;
    aspect_ratio?: string | null;
    preset: string | null;
    cost_each: number | null;
    author_name: string;
    date_create: string;
    inputs: unknown[];
    params?: {
      resolution: string | null;
      aspect_ratio: string | null;
      fit_px: number;
      prompt_mode: "auto" | "custom";
    } | null;
  };
};

export type PassportRow = { label: string; values: string[]; differs: boolean };

const DASH = "—";
const text = (v: string | null | undefined): string => (v ? v : DASH);

/** Виды, у которых `params` — умолчания сервера, а не настоящие настройки запуска. */
const NO_LAUNCH_PARAMS = new Set(["upload", "enhance", "clip"]);

/** «2026-10-22 09:27:44» / «2026-10-22T09:27:44» → «22.10 09:27»; не разобралось — как есть (без часовых поясов). */
export function formatCreated(raw: string): string {
  const m = /^\d{4}-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(raw);
  return m ? `${m[2]}.${m[1]} ${m[3]}:${m[4]}` : raw;
}

/**
 * Строки паспорта в порядке показа; `differs` — значения у карточек не все одинаковы (у «Автор» и «Поставлено» всегда false).
 * `fileSizes` — пиксели оригинала по `variant.id` («1696×2528»), известны только после загрузки картинки.
 */
export function passportRows(cards: PassportCard[], fileSizes: Record<number, string> = {}): PassportRow[] {
  const row = (label: string, get: (c: PassportCard) => string, compare = true): PassportRow => {
    const values = cards.map(get);
    return { label, values, differs: compare && new Set(values).size > 1 };
  };
  const params = (c: PassportCard) => (NO_LAUNCH_PARAMS.has(c.generation.kind ?? "") ? null : (c.generation.params ?? null));
  return [
    row("Модель", (c) => text(c.generation.model)),
    row("Разрешение", (c) => text(params(c)?.resolution)),
    row("Размер запуска", (c) => text(c.generation.size)),
    row("Файл", (c) => fileSizes[c.variant.id] ?? DASH),
    row("Пропорция", (c) => text(params(c)?.aspect_ratio ?? c.generation.aspect_ratio)),
    row("Качество", (c) => text(c.generation.quality)),
    row("Пресет", (c) => text(c.generation.preset)),
    row("Промпт", (c) => {
      const m = params(c)?.prompt_mode;
      return m === "custom" ? "Мой" : m === "auto" ? "Авто" : DASH;
    }),
    row("Ужатие входов", (c) => {
      const p = params(c);
      return p ? `${p.fit_px} px` : DASH;
    }),
    row("Входов", (c) => String(c.generation.inputs.length)),
    row("Цена за кадр", (c) => {
      const v = c.generation.cost_each;
      return v == null ? DASH : "$" + v.toFixed(4);
    }),
    row("Автор", (c) => text(c.generation.author_name), false),
    row("Поставлено", (c) => formatCreated(c.generation.date_create), false),
  ];
}

/** Доли (0..1) точки под курсором в рамке w×h; вырожденная рамка — центр. */
export function loupeOrigin(x: number, y: number, w: number, h: number): { fx: number; fy: number } {
  if (w <= 0 || h <= 0) return { fx: 0.5, fy: 0.5 };
  const clamp = (v: number) => Math.min(1, Math.max(0, v));
  return { fx: clamp(x / w), fy: clamp(y / h) };
}
